import { useState } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Seo from '@/components/feature/Seo';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import { faqList, platformStats, siteInfo, testimonials } from '@/mocks/site';

const principles = [
  {
    icon: 'ri-time-line',
    title: '按时间线整理',
    desc: '先讲清事件从哪来、怎么扩散，再谈各方回应，避免把不同阶段的说法混在一起。',
  },
  {
    icon: 'ri-links-line',
    title: '信源可追溯',
    desc: '时间线中的每条进展都标注信源与收录时间，方便你自行核对，而不是只给一个结论。',
  },
  {
    icon: 'ri-scales-3-line',
    title: '并列呈现差异',
    desc: '同一节点下不同媒体的标题与切入角度会同时列出，让报道口径的差异自然显现。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '不作立场判断',
    desc: '站内只做公开信息的聚合与整理，热度数值仅反映扩散程度，不代表对事件的定性。',
  },
];

const milestones = [
  { year: '起步期', text: '从手动整理少数几个热点开始，先把「时间线 + 信源」这套结构跑通。' },
  { year: '成长期', text: '引入分类与专题库，让长期跟踪的事件有固定落脚点，不再随讨论热度流失。' },
  { year: '现在', text: '把媒体报道对比纳入固定模块，并开放纠错与线索投稿，让时间线持续被补全。' },
];

function About() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于网报吃瓜',
      description:
        '网报吃瓜是专注热点脉络的中文资讯聚合站，按时间线整理快讯、媒体报道与转载动态。',
      inLanguage: 'zh-CN',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqList.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: `${window.location.origin}/` },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: `${window.location.origin}/about` },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title="关于我们 - 网报吃瓜的内容原则与联系方式"
        description="了解网报吃瓜的网站定位、内容原则与发展理念：按时间线整理热点，标注信源，并列呈现媒体报道差异，并提供纠错与线索投稿渠道。"
        keywords="网报吃瓜,网报吃瓜媒体,网报吃瓜资讯,关于网报吃瓜,网报吃瓜联系方式"
        canonicalPath="/about"
        schema={schema}
      />

      <div className="w-full px-4 pt-24 md:px-8 md:pt-32">
        <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

        <header className="mt-6 border-b border-background-200/80 pb-8">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
            <i className="ri-information-line" />
            关于我们
          </span>
          <h1 className="mt-4 font-heading text-3xl font-bold leading-snug text-foreground-950 md:text-4xl">
            网报吃瓜：把一件事的前因后果讲清楚
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {siteInfo.intro}
          </p>
        </header>
      </div>

      <section className="w-full px-4 py-12 md:px-8 md:py-16" aria-label="品牌定位">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-14">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950">网站定位</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              网报吃瓜服务的是那些不愿被碎片信息牵着走的人。我们不追求把每条消息都推到你面前，而是希望你在看过之后，能对一件事形成自己的判断。
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              因此站内的重点始终放在两件事上：把事件按顺序排清楚，把各方说法并列摆出来。快讯负责「新」，专题负责「全」，两条线配合使用。
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {platformStats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-lg border border-background-200/80 bg-background-100 p-4 md:p-5"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-xl text-primary-700">
                  <i className={stat.icon} />
                </span>
                <div className="mt-3 font-heading text-lg font-bold text-foreground-950 md:text-xl">
                  {stat.value}
                </div>
                <div className="mt-1 text-xs text-foreground-600 md:text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        className="w-full border-y border-background-200/80 bg-background-100 px-4 py-12 md:px-8 md:py-16"
        aria-label="内容原则"
      >
        <h2 className="font-heading text-2xl font-bold text-foreground-950">我们坚持的内容原则</h2>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
          这些原则决定了站内信息怎么被收录、排序与呈现，也是我们判断一条线索值不值得跟进的标准。
        </p>

        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {principles.map((item) => (
            <article
              key={item.title}
              className="rounded-lg border border-background-200/80 bg-background-50 p-5"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-xl text-accent-700">
                <i className={item.icon} />
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                {item.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="w-full px-4 py-12 md:px-8 md:py-16" aria-label="发展理念">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[320px_1fr] lg:gap-14">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950">发展理念</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              热点会过去，但整理过的时间线会留下。我们更愿意做那个持续补全记录的角色，而不是在讨论最热时再添一把火。
            </p>
          </div>
          <ol className="relative">
            {milestones.map((item, index) => (
              <li key={item.year} className="relative flex gap-5 pb-8 last:pb-0">
                <div className="relative flex shrink-0 flex-col items-center">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary-500 text-xs font-bold text-background-50">
                    {index + 1}
                  </span>
                  {index !== milestones.length - 1 && (
                    <span className="mt-1 w-px flex-1 bg-background-300" />
                  )}
                </div>
                <div>
                  <span className="font-heading text-sm font-bold text-foreground-950">
                    {item.year}
                  </span>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section
        className="w-full border-y border-background-200/80 bg-background-100 px-4 py-12 md:px-8 md:py-16"
        aria-label="用户口碑"
      >
        <h2 className="font-heading text-2xl font-bold text-foreground-950">用户怎么说</h2>
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
          {testimonials.map((item) => (
            <figure
              key={item.name}
              className="flex h-full flex-col rounded-lg border border-background-200/80 bg-background-50 p-5"
            >
              <i className="ri-double-quotes-l text-3xl text-primary-300" />
              <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                {item.text}
              </blockquote>
              <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200/80 pt-4">
                <span className="h-10 w-10 shrink-0 overflow-hidden rounded-full">
                  <img
                    src={item.avatar}
                    alt={`${item.name} 的头像`}
                    title={`${item.name} - 网报吃瓜用户口碑`}
                    className="h-full w-full object-cover object-top"
                  />
                </span>
                <span>
                  <span className="block text-sm font-medium text-foreground-950">{item.name}</span>
                  <span className="block text-xs text-foreground-500">{item.role}</span>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="w-full px-4 py-12 md:px-8 md:py-16" aria-label="常见问题">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[320px_1fr] lg:gap-14">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950">常见问题</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              关于收录范围、更新频率与热度算法，这里先回答最常见的几个问题。没找到答案的话，可以直接给我们发邮件。
            </p>
          </div>
          <div className="space-y-3">
            {faqList.map((item, index) => {
              const open = openIndex === index;
              return (
                <div
                  key={item.q}
                  className="overflow-hidden rounded-lg border border-background-200/80 bg-background-50"
                >
                  <button
                    type="button"
                    onClick={() => setOpenIndex(open ? null : index)}
                    aria-expanded={open}
                    className="flex w-full cursor-pointer items-center justify-between gap-4 px-5 py-4 text-left"
                  >
                    <h3 className="font-heading text-sm font-bold text-foreground-950 md:text-base">
                      {item.q}
                    </h3>
                    <i
                      className={`shrink-0 text-lg text-foreground-500 transition-transform ${
                        open ? 'ri-subtract-line' : 'ri-add-line'
                      }`}
                    />
                  </button>
                  {open && (
                    <p className="border-t border-background-200/80 px-5 py-4 text-sm leading-relaxed text-foreground-600">
                      {item.a}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="w-full px-4 pb-16 md:px-8 md:pb-24" aria-label="联系方式">
        <div className="relative overflow-hidden rounded-lg bg-secondary-950 px-5 py-10 md:px-12 md:py-14">
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary-500/20" />
          <div className="relative grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/10 px-3 py-1 text-xs text-primary-300">
                <i className="ri-customer-service-2-line" />
                联系我们
              </span>
              <h2 className="mt-4 font-heading text-2xl font-bold text-background-50 md:text-3xl">
                纠错、投稿与媒体合作
              </h2>
              <p className="mt-4 max-w-lg text-sm leading-relaxed text-background-200/85">
                {siteInfo.contactNote}
              </p>
            </div>
            <div className="flex flex-col justify-center gap-4">
              <a
                href={`mailto:${siteInfo.email}`}
                className="flex items-center gap-4 rounded-lg bg-background-50/10 p-5 transition-colors hover:bg-background-50/15"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-xl text-foreground-950">
                  <i className="ri-mail-send-line" />
                </span>
                <span>
                  <span className="block text-xs text-background-300/80">联系邮箱</span>
                  <span className="mt-0.5 block text-sm font-medium text-background-50">
                    {siteInfo.email}
                  </span>
                </span>
              </a>
              <div className="flex items-center gap-4 rounded-lg bg-background-50/10 p-5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent-500 text-xl text-background-50">
                  <i className="ri-time-line" />
                </span>
                <span>
                  <span className="block text-xs text-background-300/80">更新时段</span>
                  <span className="mt-0.5 block text-sm font-medium text-background-50">
                    {siteInfo.updateFreq}
                  </span>
                </span>
              </div>
              <Link
                to="/feed"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-foreground-950 transition-colors hover:bg-primary-400"
              >
                <i className="ri-flashlight-fill" />
                先去看看最新快讯
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

export default About;